/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Select,
  Input,
  Checkbox,
  Form,
  message,
  AutoComplete,
  Alert,
  Typography,
} from 'antd';
import ModalWrapper from './ModalWrapper';
import { captureAnalytics } from '../../actions/preload';
import { categoryLabel } from '../../utils/taskTemplateAutomation';
import './settings-modal.scss';

const sectionTitleStyle = {
  fontSize: '13px',
  fontWeight: 600,
  color: '#181818',
  margin: '0 0 2px',
};

const sectionHelpStyle = {
  fontSize: '12px',
  color: '#5c5c5c',
  display: 'block',
  marginBottom: '8px',
};

const sectionStyle = {
  marginBottom: '16px',
};

// Assignees the auto-upload worker resolves per document from the extracted
// ordering/referring provider (EmaTaskTemplate::ORDERING_PROVIDER_ASSIGNEES).
const ORDERING_PROVIDER_ASSIGNEES = ['Ordering Provider', 'Ordering Provider Group'];

export class AddTaskTemplate extends Component {
  // A provider is "blank" when nothing real is selected. Matches the backend's
  // trim-aware check (EmaTaskTemplate#assign_to_present?) so the UI and API agree
  // on when a template is manual-only.
  static isProviderBlank(assignTo) {
    if (!assignTo) return true;
    const list = Array.isArray(assignTo) ? assignTo : [assignTo];
    return !list.some((value) => value != null && String(value).trim() !== '');
  }

  // Only a lone Ordering Provider marker is matched per document; mixed with
  // fixed names the worker treats the whole list as fixed assignees.
  static isOrderingProviderAssignment(assignTo) {
    const list = Array.isArray(assignTo) ? assignTo : [assignTo];
    return list.length === 1 && ORDERING_PROVIDER_ASSIGNEES.includes(list[0]);
  }

  static isOrderingProviderGroupAssignment(assignTo) {
    const list = Array.isArray(assignTo) ? assignTo : [assignTo];
    return list.length === 1 && list[0] === 'Ordering Provider Group';
  }

  constructor(props) {
    super(props);
    this.isTebra = localStorage.getItem('m8_auto_upload_emr') === 'Kareo';
    this.priorityOptions = ['Normal', 'High', 'STAT'];
    this.taskOptions = [
      'Authorization', 'Communication', 'Fax', 'Notify', 'Obtain', 'Order', 'Pathology', 'Prescribe', 'Reassure',
      'Recall', 'Re-Evaluate', 'Refer', 'Return To Clinic', 'Review', 'Schedule', 'Sign', 'Test',
    ];

    this.taskDetailsOptions = [
      'Additional Testing', 'Benign', 'Claim Review', 'Discuss at Follow-up', 'Follow-up Appt', 'Labs', 'PCP',
      'Medication', 'Normal', 'No Further Treatment', 'Procedure', 'Referring Provider', 'Results', 'Sign/Cosign',
      'Surgery',
    ];

    const { template, userDepartments, userOrganization } = props.modalType.data;
    const currentUserName = localStorage.getItem('m8_name');

    // One flat "audience" list replaces the old Scope + Scope Name dropdowns.
    // Each option carries the scope/scope_name pair the API expects.
    this.audienceOptions = [
      {
        key: `User::${currentUserName}`,
        scope: 'User',
        scopeName: currentUserName,
        label: `Only me (${currentUserName})`,
      },
      ...(userDepartments || []).map((name) => ({
        key: `Department::${name}`,
        scope: 'Department',
        scopeName: name,
        label: `Department: ${name}`,
      })),
      ...(userOrganization ? [{
        key: `Organization::${userOrganization}`,
        scope: 'Organization',
        scopeName: userOrganization,
        label: `Entire organization (${userOrganization})`,
      }] : []),
    ];
    // Editing a template owned by someone outside the current options (e.g. an
    // admin editing another user's personal template) — keep it selectable.
    if (template.scope && template.scope_name
      && !this.audienceOptions.some((option) => option.scope === template.scope && option.scopeName === template.scope_name)) {
      this.audienceOptions.unshift({
        key: `${template.scope}::${template.scope_name}`,
        scope: template.scope,
        scopeName: template.scope_name,
        label: template.scope === 'User' ? `Only ${template.scope_name}` : `${template.scope}: ${template.scope_name}`,
      });
    }

    this.state = {
      templateTaskName: template.task_name,
      editingTemplateID: '',
      task: template.task_type,
      searchedTasks: this.taskOptions,
      taskDetails: template.task_details,
      taskDetailsSearchOptions: this.taskDetailsOptions,
      priority: template.priority,
      notes: template.notes,
      assignTo: template.assign_to_as_array || undefined,
      fallbackAssignTo: template.fallback_assign_to_as_array || [],
      fallbackToUser: !!template.fallback_to_user,
      searchedNames: null,
      audienceKey: (template.scope && template.scope_name) ? `${template.scope}::${template.scope_name}` : undefined,
      documentCategory: categoryLabel(template.emr_category, template.ai_category_name),
      documentCategoryId: template.document_category_id,
      dedupeEnabled: !!template.dedupe_enabled,
      triggerClearedByAudience: false,
      triggerClearedByProvider: false,
      does: true,
      filterParams: 'contains',
      filterString: '',
      addMoreFilters: false,
    };
  }

  async componentDidMount() {
    const { modalType } = this.props;
    const { template, isAdmin } = modalType.data;
    const {
      getEmaTaskProviders,
      getDocumentCategories,
      getTaskTemplateCategoryUsage,
    } = this.props;
    if (this.isTebra) {
      await this.props.getKareoTaskProviders();
    } else {
      await getEmaTaskProviders();
    }
    await getDocumentCategories();
    if (isAdmin) {
      await getTaskTemplateCategoryUsage();
    }
    this.setState({ searchedNames: this.isTebra ? this.props.allKareoTaskProviders : this.props.allEmaTaskProviders });
    const { assignTo } = this.state;
    if (assignTo) {
      this.setState({ assignTo: [...assignTo].sort() });
    }
    if (template.id) {
      this.setState({ editingTemplateID: template.id });
    }
    if (template.assign_to_filters && template.assign_to_filters.length > 0) {
      this.setState({
        does: template.assign_to_filters[0].does,
        filterParams: template.assign_to_filters[0].param,
        filterString: template.assign_to_filters[0].string,
        addMoreFilters: true,
      });
    }
  }

  getModalTitle() {
    const { modalType: { data: { isDuplicateMode = false } } } = this.props;
    const { editingTemplateID } = this.state;

    const kind = this.isTebra ? 'Note' : 'Task';
    if (editingTemplateID) return `Edit ${kind} Template`;
    if (isDuplicateMode) return `Duplicate ${kind} Template`;
    return `Add New ${kind} Template`;
  }

  getSelectedAudience() {
    const { audienceKey } = this.state;
    return this.audienceOptions.find((option) => option.key === audienceKey) || null;
  }

  handleAssignToChange = (assignTo) => {
    if (this.isTebra) {
      captureAnalytics('tebra_note_template_assignee_changed', { has_assignee: !!assignTo });
    }
    const providerBlank = AddTaskTemplate.isProviderBlank(assignTo);
    // Emptying the provider on a triggered template makes it manual-only, so drop the trigger.
    const clearingTrigger = providerBlank && !!this.state.documentCategoryId;
    if (clearingTrigger) {
      captureAnalytics('task_template_trigger_cleared_by_blank_provider', {});
    }
    this.setState({
      assignTo,
      ...(clearingTrigger
        ? { documentCategoryId: null, documentCategory: null, triggerClearedByProvider: true }
        : {}),
      ...(providerBlank ? {} : { triggerClearedByProvider: false }),
    });
  };

  getTriggerUnavailableReason(audience, providerMissing) {
    if (!audience) {
      return 'Pick an audience first — available triggers depend on it.';
    }
    if (audience.scope === 'User') {
      return 'Personal templates can\'t run automatically. Pick a department or organization audience to add a trigger.';
    }
    // Department/organization audience but no provider assigned.
    if (providerMissing) {
      return 'Templates with no assigned provider are manual-only. Add a provider below to enable an automatic trigger.';
    }
    return '';
  }

  getSortedCategories() {
    const { allDocumentCategories } = this.props;
    const categories = allDocumentCategories?.categories || [];
    return [...categories].sort((a, b) => {
      // Blank EMA names sort first; comparing null directly is not a total order
      // (both > comparisons are false), which leaves the order undefined.
      const emrA = a.emr_category || '';
      const emrB = b.emr_category || '';
      if (emrA !== emrB) return emrA > emrB ? 1 : -1;
      // Two categories can share an EMA name while pointing at different AI
      // categories; fall through to the AI name and then the id so the order
      // does not shuffle between renders.
      const aiA = a.ai_category_name || '';
      const aiB = b.ai_category_name || '';
      if (aiA !== aiB) return aiA > aiB ? 1 : -1;
      return a.id - b.id;
    });
  }

  // Trigger usage for the selected audience, excluding the template being edited.
  // Each category can only trigger one template per audience (unique on the backend),
  // so these become greyed-out options rather than silently disappearing ones.
  getAudienceCategoryUsage() {
    const { taskTemplateCategoryUsage } = this.props;
    const { editingTemplateID } = this.state;
    const audience = this.getSelectedAudience();
    if (!audience || audience.scope === 'User') return [];
    return (taskTemplateCategoryUsage || []).filter((usage) => (
      usage.owner_type === audience.scope
      && usage.scope_name === audience.scopeName
      && usage.template_id !== editingTemplateID
    ));
  }

  getTriggerConflict() {
    const { documentCategoryId } = this.state;
    if (!documentCategoryId) return null;
    return this.getAudienceCategoryUsage()
      .find((usage) => usage.document_category_id === documentCategoryId) || null;
  }

  // Templates for OTHER audiences triggered by the selected category. Allowed —
  // each fax line fires only the template whose audience owns it — but worth
  // explaining so two same-category templates don't look like a double-fire bug.
  getCrossAudienceUsage() {
    const { taskTemplateCategoryUsage } = this.props;
    const { documentCategoryId, editingTemplateID } = this.state;
    const audience = this.getSelectedAudience();
    if (!documentCategoryId || !audience || audience.scope === 'User') return [];
    return (taskTemplateCategoryUsage || []).filter((usage) => (
      usage.document_category_id === documentCategoryId
      && usage.template_id !== editingTemplateID
      && !(usage.owner_type === audience.scope && usage.scope_name === audience.scopeName)
    ));
  }

  getPayloadDocumentCategoryId(audience) {
    const { modalType: { data: { template } } } = this.props;
    const { documentCategoryId, assignTo } = this.state;
    if (!audience) return null;
    // Templates with no assigned provider are manual-only — never send a trigger.
    if (AddTaskTemplate.isProviderBlank(assignTo)) return null;
    if (audience.scope !== 'User') return documentCategoryId;

    // Legacy personal templates can have triggers from API/seeded data. Preserve
    // that value on edit, but keep create and audience-switch payloads triggerless.
    const preservingExistingPersonalTrigger = template.id
      && template.scope === 'User'
      && template.document_category_id != null
      && documentCategoryId === template.document_category_id;
    return preservingExistingPersonalTrigger ? documentCategoryId : null;
  }

  buildTemplatePayload = () => {
    const {
      templateTaskName,
      assignTo,
      task,
      taskDetails,
      priority,
      addMoreFilters,
      does,
      filterParams,
      filterString,
      notes,
      dedupeEnabled,
      fallbackAssignTo,
      fallbackToUser,
    } = this.state;
    const audience = this.getSelectedAudience();
    // Column is NOT NULL, so a blank provider must post an empty array, not undefined.
    let assignToArray = [];
    if (Array.isArray(assignTo)) {
      assignToArray = assignTo;
    } else if (assignTo) {
      assignToArray = [assignTo];
    }
    // Collapse blank/whitespace entries so an all-blank value (e.g. a legacy or
    // API-created ['']) persists as [] — keeping the stored value, the serializer
    // output, and the length-based provider gates in the other task flows
    // (AutoUpload, CreateNewChart) consistent with isProviderBlank.
    assignToArray = assignToArray.filter((value) => value != null && String(value).trim() !== '');
    return {
      task_name: templateTaskName.trim(),
      task_type: this.isTebra ? 'Note' : task,
      assign_to: assignToArray,
      // The fallback only applies to Ordering Provider templates; clear it when
      // the assignee changes to a fixed name so the API doesn't reject the save.
      fallback_assign_to: this.shouldShowFallbackAssignee(assignToArray) ? (fallbackAssignTo || []) : [],
      fallback_to_user: !this.isTebra && AddTaskTemplate.isOrderingProviderGroupAssignment(assignToArray) && !!fallbackToUser,
      task_details: this.isTebra ? '' : taskDetails,
      priority: this.isTebra ? 'Normal' : priority,
      scope: audience?.scope,
      scope_name: audience?.scopeName,
      document_category_id: this.getPayloadDocumentCategoryId(audience),
      dedupe_enabled: this.shouldShowDedupeControl() && !!dedupeEnabled,
      notes,
      assign_to_filters: (!this.isTebra && addMoreFilters && filterString !== '') ? [{
        does,
        param: filterParams,
        string: filterString,
      }] : [],
    };
  };

  // The hosting page owns the table's search/filter/page state, so let it
  // refetch in place when it offers a callback; the bare list fetch is only the
  // fallback for callers that opened the dialog without one.
  refreshTemplateList = () => {
    const { getTaskTemplates, modalType } = this.props;
    const onSaved = modalType?.data?.onSaved;
    if (typeof onSaved === 'function') {
      onSaved();
      return;
    }
    getTaskTemplates();
  };

  addTemplate = async () => {
    const { createNewTaskTemplate, hideModal } = this.props;
    const { templateTaskName } = this.state;
    if (!templateTaskName || templateTaskName.trim() === '') return;
    const payload = this.buildTemplatePayload();
    const result = await createNewTaskTemplate(payload);
    if (result?.error) {
      message.error({
        content: result.response?.data?.error || 'Error adding template. Please try again.',
      });
      return;
    }
    captureAnalytics('task_template_created', {
      manual_only: AddTaskTemplate.isProviderBlank(this.state.assignTo),
      has_trigger: !!payload.document_category_id,
      scope: payload.scope,
    });
    hideModal();
    message.success({
      content: `${this.isTebra ? 'Note' : 'Task'} Template successfully added`,
    });
    this.refreshTemplateList();
  };

  editTemplate = async () => {
    const { updateTaskTemplate, hideModal } = this.props;
    const { editingTemplateID } = this.state;
    const payload = this.buildTemplatePayload();
    const result = await updateTaskTemplate(editingTemplateID, payload);
    if (result?.error) {
      message.error({
        content: result.response?.data?.error || 'Error updating template. Please try again.',
      });
      return;
    }
    captureAnalytics('task_template_updated', {
      manual_only: AddTaskTemplate.isProviderBlank(this.state.assignTo),
      has_trigger: !!payload.document_category_id,
      scope: payload.scope,
    });
    hideModal();
    message.success({
      content: `${this.isTebra ? 'Note' : 'Task'} Template successfully updated`,
    });
    this.refreshTemplateList();
  };

  handleAudienceChange = (key) => {
    const option = this.audienceOptions.find((audience) => audience.key === key);
    const { documentCategoryId } = this.state;
    const clearingTrigger = option?.scope === 'User' && !!documentCategoryId;
    this.setState({
      audienceKey: key,
      // Switching audience is a fresh context — clear the blank-provider notice too,
      // so it can't linger after the reason for it no longer applies.
      triggerClearedByProvider: false,
      ...(clearingTrigger
        ? { documentCategoryId: null, documentCategory: null, triggerClearedByAudience: true }
        : { triggerClearedByAudience: false }),
    });
  };

  handleDocumentCategoryChange = (value) => {
    const category = this.getSortedCategories().find((docCategory) => docCategory.id === value);
    this.setState((prevState) => ({
      documentCategoryId: category ? category.id : null,
      documentCategory: category ? categoryLabel(category.emr_category, category.ai_category_name) : null,
      dedupeEnabled: this.taskDedupEligibleCategory(category) ? prevState.dedupeEnabled : false,
      triggerClearedByAudience: false,
    }));
  };

  handleDedupeChange = (event) => {
    const enabled = event.target.checked;
    captureAnalytics('task_dedup_template_dedupe_toggled', { enabled });
    this.setState({ dedupeEnabled: enabled });
  };

  handleFallbackToUserChange = (event) => {
    const enabled = event.target.checked;
    this.setState({ fallbackToUser: enabled });
    captureAnalytics('task_template_fallback_to_user_toggled', { enabled });
  };

  handleFallbackAssignToChange = (value) => {
    const fallbackAssignTo = value || [];
    this.setState({ fallbackAssignTo });
    captureAnalytics('task_template_fallback_assignee_changed', { assignee_count: fallbackAssignTo.length });
  };

  shouldShowFallbackAssignee(assignTo = this.state.assignTo) {
    return !this.isTebra && AddTaskTemplate.isOrderingProviderAssignment(assignTo);
  }

  taskDedupEligibleCategory(category) {
    return !!category?.task_dedup_eligible;
  }

  shouldShowDedupeControl() {
    if (this.isTebra) return false;
    const { documentCategoryId } = this.state;
    const category = this.getSortedCategories().find(({ id }) => id === documentCategoryId);
    return this.taskDedupEligibleCategory(category);
  }

  renderAudienceSection() {
    const { audienceKey } = this.state;

    return (
      <div style={sectionStyle}>
        <p style={sectionTitleStyle}>1. Who can use this template?</p>
        <Typography.Text style={sectionHelpStyle}>
          {`Controls who sees this template in Send to Chart and Create ${this.isTebra ? 'Note' : 'Task'} — and which fax lines can trigger it.`}
        </Typography.Text>
        <Form.Item required>
          <Select
            placeholder="Pick an audience"
            value={audienceKey}
            onChange={this.handleAudienceChange}
            options={this.audienceOptions.map((option) => ({ label: option.label, value: option.key }))}
          />
        </Form.Item>
      </div>
    );
  }

  renderTriggerSection() {
    const { modalType, taskAutoUploadEnabled } = this.props;
    const { isDuplicateMode = false, duplicatedFromCategory } = modalType.data;
    const {
      documentCategory,
      documentCategoryId,
      triggerClearedByAudience,
      triggerClearedByProvider,
      assignTo,
    } = this.state;

    const audience = this.getSelectedAudience();
    const providerMissing = AddTaskTemplate.isProviderBlank(assignTo);
    const triggerUnavailable = !audience || audience.scope === 'User' || providerMissing;
    const usedBy = new Map(this.getAudienceCategoryUsage()
      .map((usage) => [usage.document_category_id, usage.task_name]));
    const conflict = this.getTriggerConflict();
    const crossAudienceUsage = conflict ? [] : this.getCrossAudienceUsage();
    const faxLineLabel = audience && audience.scope === 'Department'
      ? `fax lines owned by ${audience.scopeName}`
      : 'fax lines owned by your organization';

    return (
      <div style={sectionStyle}>
        <p style={sectionTitleStyle}>
          2. When should it run automatically?
          {' '}
          <span style={{ fontWeight: 400, color: '#8c8c8c' }}>(optional)</span>
        </p>
        <Typography.Text style={sectionHelpStyle}>
          Medsender AI reads each incoming fax and assigns it one of your document categories
          {`(from the Categories tab). Pick the category that should create this ${this.isTebra ? 'note' : 'task'}.`}
        </Typography.Text>
        {!taskAutoUploadEnabled && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 8 }}
            message="Task automation isn't enabled for your organization yet — this template will be manual-only."
          />
        )}
        {isDuplicateMode && duplicatedFromCategory && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 8 }}
            message={`The trigger ("${duplicatedFromCategory}") wasn't copied — each document category can only fire one template per audience.`}
          />
        )}
        {triggerClearedByAudience && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 8 }}
            message="The trigger was removed because personal templates can't run automatically."
          />
        )}
        {triggerClearedByProvider && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 8 }}
            message="The trigger was removed because templates with no assigned provider are manual-only."
          />
        )}
        {triggerUnavailable ? (
          <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
            {this.getTriggerUnavailableReason(audience, providerMissing)}
          </Typography.Text>
        ) : (
          <Form.Item
            label="Trigger — when a fax's document category is"
            validateStatus={conflict ? 'error' : ''}
            help={conflict
              ? `"${documentCategory}" is already used by "${conflict.task_name}" for this audience. Pick another category or edit that template instead.`
              : `Fires when the AI classifies a fax received on ${faxLineLabel} into this category. Needs task automation on for your organization and a provider assigned above.`}
            style={{ marginBottom: '24px' }}
          >
            <Select
              showSearch
              allowClear
              optionFilterProp="children"
              placeholder="No trigger — manual use only"
              value={documentCategoryId}
              onChange={this.handleDocumentCategoryChange}
            >
              {this.getSortedCategories().map((category) => {
                const inUseBy = category.id === documentCategoryId ? null : usedBy.get(category.id);
                return (
                  <Select.Option key={category.id} value={category.id} disabled={!!inUseBy}>
                    {inUseBy
                      ? `${categoryLabel(category.emr_category, category.ai_category_name)} — in use by "${inUseBy}"`
                      : categoryLabel(category.emr_category, category.ai_category_name)}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
        )}
        {crossAudienceUsage.length > 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 8 }}
            message={`"${documentCategory}" also triggers ${crossAudienceUsage
              .map((usage) => `"${usage.task_name}" (${usage.owner_type === 'Organization' ? 'entire organization' : `Department: ${usage.scope_name}`})`)
              .join(', ')}. That's OK — each fax line only fires the template for the audience that owns it, so tasks won't be created twice.`}
          />
        )}
        {this.shouldShowDedupeControl() && (
          <Form.Item style={{ marginBottom: '24px' }}>
            <Checkbox
              checked={this.state.dedupeEnabled}
              onChange={this.handleDedupeChange}
            >
              Skip duplicate refill tasks
            </Checkbox>
            <Typography.Text style={{ ...sectionHelpStyle, marginTop: 4, marginBottom: 0 }}>
              When a refill for the same patient and medication was already tasked in the last 7 days,
              no new task is created. The fax stays in the inbox flagged "Task skipped — possible duplicate"
              so staff can review it. Takes effect once duplicate detection is enabled for your organization.
            </Typography.Text>
          </Form.Item>
        )}
      </div>
    );
  }

  // What happens when the document's provider can't be matched in EMA. Both
  // options are opt-in per template; with neither, the upload fails as before.
  renderUnmatchedProviderSection = (providers) => {
    const { fallbackAssignTo, fallbackToUser } = this.state;
    const isGroupTemplate = AddTaskTemplate.isOrderingProviderGroupAssignment(this.state.assignTo);

    return (
      <div style={sectionStyle}>
        <p style={sectionTitleStyle}>If the provider can&apos;t be matched in EMA</p>
        <Typography.Text style={sectionHelpStyle}>
          By default the upload fails and someone assigns the task by hand.
        </Typography.Text>
        {isGroupTemplate && (
          <Form.Item style={{ marginBottom: '12px' }}>
            <Checkbox
              checked={!!fallbackToUser}
              onChange={this.handleFallbackToUserChange}
            >
              Assign to the provider&apos;s own EMA user when no staff group matches
            </Checkbox>
            <Typography.Text style={{ ...sectionHelpStyle, marginTop: 4, marginBottom: 0 }}>
              For providers without a group, such as PAs and NPs.
            </Typography.Text>
          </Form.Item>
        )}
        <Form.Item
          label={isGroupTemplate ? 'Otherwise, assign to (optional)' : 'Assign to (optional)'}
          extra="Used when the document names no provider, or no one matches in EMA. Leave blank to fail the upload."
        >
          <Select
            mode="multiple"
            showSearch
            allowClear
            placeholder="Select a fallback provider or group"
            optionFilterProp="children"
            onChange={this.handleFallbackAssignToChange}
            value={fallbackAssignTo}
          >
            {(providers || []).reduce((options, provider) => {
              const name = typeof provider === 'string' ? provider : provider?.name;
              if (name && !ORDERING_PROVIDER_ASSIGNEES.includes(name)) {
                options.push(<Select.Option key={name} value={name}>{name}</Select.Option>);
              }
              return options;
            }, [])}
          </Select>
        </Form.Item>
      </div>
    );
  };

  renderTaskSection = () => {
    const { allEmaTaskProviders, modalType } = this.props;
    const providers = this.isTebra ? this.props.allKareoTaskProviders : allEmaTaskProviders;
    const { isAdmin } = modalType.data;
    const {
      templateTaskName,
      task,
      taskDetails,
      assignTo,
      priority,
      does,
      filterParams,
      filterString,
      notes,
      addMoreFilters,
    } = this.state;

    const handleAssignToSearch = (e) => {
      if (e) {
        const filtered = providers.filter((provider) => {
          const providerName = typeof provider === 'string' ? provider : provider?.name;
          return providerName && providerName.toLowerCase().includes(e.toLowerCase());
        });
        this.setState({ searchedNames: filtered });
      } else {
        this.setState({ searchedNames: providers });
      }
    };

    const handleTaskSearch = (e) => {
      if (e) {
        const filtered = this.taskOptions.filter((tasktype) => tasktype.toLowerCase().includes(e.toLowerCase()));
        this.setState({ searchedTasks: filtered });
      } else {
        this.setState({ searchedTasks: this.taskOptions });
      }
    };

    const handleTaskChange = (e) => {
      this.setState({ task: e });
    };

    const handleSearchTaskDetails = (e) => {
      this.setState({ taskDetails: e });
      const filteredOptions = this.taskDetailsOptions.filter((option) => (
        option.toLowerCase().includes(e.toLowerCase())));
      this.setState({ taskDetailsSearchOptions: filteredOptions });
    };

    const handleTaskDetailsSelection = (e) => {
      this.setState({ taskDetails: e });
    };

    const handlePriorityChange = (e) => {
      this.setState({ priority: e });
    };

    const toggleAddMoreFiltersChange = () => {
      this.setState((prevState) => ({ addMoreFilters: !prevState.addMoreFilters }));
    };

    const handleDoesChange = (e) => {
      this.setState({ does: e });
    };

    const handleFilterParamsChange = (e) => {
      this.setState({ filterParams: e });
    };

    const handleFilterStringChange = (e) => {
      this.setState({ filterString: e.target.value });
    };

    const handleNotesChange = (e) => {
      this.setState({ notes: e.target.value });
    };

    return (
      <div style={sectionStyle}>
        {isAdmin && (
          <p style={sectionTitleStyle}>{this.isTebra ? '3. What note should Tebra create?' : '3. What task should EMA create?'}</p>
        )}
        <Form.Item
          hasFeedback
          required
          label={this.isTebra ? 'Note Template Name' : 'Template Task Name'}
          extra={`The name users pick in Send to Chart and Create ${this.isTebra ? 'Note' : 'Task'}.`}
          rules={[
            {
              required: true,
              message: 'Must provide a template task name',
            },
          ]}
        >
          <Input
            value={templateTaskName}
            placeholder={templateTaskName}
            onChange={(event) => this.setState({ templateTaskName: event.target.value })}
          />
        </Form.Item>
        {!this.isTebra && (
        <div style={{ display: 'flex', gap: '10px' }}>
          <Form.Item
            label="Task"
            required
            style={{ flex: 0.5 }}
          >
            <Select
              showSearch
              optionFilterProp="children"
              placeholder="Task"
              onChange={handleTaskChange}
              onSearch={handleTaskSearch}
              filterOption={false}
              value={task}
            >
              {this.state.searchedTasks.map((e) => <Select.Option key={e} value={e}>{e}</Select.Option>)}
            </Select>

          </Form.Item>

          <Form.Item
            label="Task Details"
            required
            style={{ flex: 0.5 }}
          >
            <AutoComplete
              placeholder="Task Details"
              value={taskDetails}
              options={this.state.taskDetailsSearchOptions.map((option) => ({ value: option }))}
              onSearch={handleSearchTaskDetails}
              onSelect={handleTaskDetailsSelection}
            />
          </Form.Item>
        </div>
        )}
        <div style={{ display: 'flex', gap: '10px' }}>
          <Form.Item
            label={this.isTebra ? 'Assign to' : 'Assign to (optional)'}
            required={this.isTebra}
            extra={this.isTebra ? 'Choose one synced Tebra user. The user must be active on the fax line’s EMR connection.' : 'Leave blank to make this template manual-only — users must pick a provider when creating the task.'}
            style={{ flex: 0.5 }}
          >
            <Select
              mode={this.isTebra ? undefined : 'multiple'}
              showSearch
              placeholder="Select a provider"
              optionFilterProp="children"
              onChange={this.handleAssignToChange}
              onSearch={handleAssignToSearch}
              filterOption={false}
              value={this.isTebra && Array.isArray(assignTo) ? assignTo[0] : assignTo}
              allowClear
            >
              {this.state.searchedNames && this.state.searchedNames.map((e, index) => {
                const providerName = typeof e === 'string' ? e : e?.name;
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <Select.Option key={index} value={providerName}>
                    {providerName}
                  </Select.Option>
                );
              })}
            </Select>

          </Form.Item>

          {!this.isTebra && (
          <Form.Item
            label="Priority"
            required
            style={{ flex: 0.5 }}
          >
            <Select
              allowClear={false}
              options={this.priorityOptions.map((option) => ({ value: option }))}
              onChange={handlePriorityChange}
              placeholder="Priority"
              value={priority}
            />
          </Form.Item>
          )}
        </div>
        {
          !this.isTebra && assignTo?.includes('Ordering Provider Group') && (
            <div style={{ marginBottom: '16px' }}>
              <Typography.Text style={{ color: '#1890ff', cursor: 'pointer' }} onClick={toggleAddMoreFiltersChange}>
                {addMoreFilters ? '-' : '+'}
                {' '}
                Provider Group Filter
              </Typography.Text>
              {addMoreFilters && (
              <div style={{ display: 'flex', gap: '10px' }}>
                <Form.Item
                  style={{ flex: 0.5 }}
                >
                  <Select
                    allowClear={false}
                    options={[{ label: 'Does', value: true }, { label: 'Does Not', value: false }]}
                    onChange={handleDoesChange}
                    label="Does"
                    value={does}
                  />
                </Form.Item>
                <Form.Item
                  style={{ flex: 0.5 }}
                >
                  <Select
                    allowClear={false}
                    options={[{ label: 'Contain', value: 'contains' }, { label: 'Start With', value: 'starts_with' }, { label: 'End With', value: 'ends_with' }]}
                    onChange={handleFilterParamsChange}
                    value={filterParams}
                  />
                </Form.Item>
                <Form.Item
                  style={{ flex: 0.5 }}
                >
                  <Input
                    placeholder="String"
                    value={filterString}
                    onChange={handleFilterStringChange}
                    allowClear
                  />
                </Form.Item>
              </div>
              )}
            </div>
          )
        }
        {this.shouldShowFallbackAssignee() && this.renderUnmatchedProviderSection(providers)}
        <Form.Item
          label={this.isTebra ? 'Note text' : 'Notes (optional)'}
        >
          <Input.TextArea
            placeholder="Notes"
            value={notes}
            onChange={handleNotesChange}
          />
        </Form.Item>
        {!isAdmin && (
          <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
            Want this task created automatically when a fax arrives? An organization admin can add
            a document category trigger to a department or organization template.
          </Typography.Text>
        )}
      </div>
    );
  };

  renderModal = () => {
    const { isLoading, modalType } = this.props;
    const { isAdmin } = modalType.data;
    const { editingTemplateID } = this.state;

    if (isLoading) {
      return (<span> Adding new template... </span>);
    }
    return (
      <Form
        layout="vertical"
        onFinish={!editingTemplateID && this.addTemplate}
        id="addTemplate"
      >
        {isAdmin && this.renderAudienceSection()}
        {isAdmin && this.renderTriggerSection()}
        {this.renderTaskSection()}
      </Form>
    );
  };

  render() {
    const { isLoading, hideModal, modalType: { data: { isAdmin } } } = this.props;
    const {
      editingTemplateID,
      templateTaskName,
      task,
      taskDetails,
      audienceKey,
      priority,
    } = this.state;
    const customContentStyle = {
      width: '560px',
    };

    return (
      <ModalWrapper
        wrapClassName="settings-modal-wrap"
        hideModal={hideModal}
        customContentStyle={customContentStyle}
        action={!editingTemplateID ? this.addTemplate : this.editTemplate}
        actionName={editingTemplateID ? 'Save' : 'Add'}
        dismiss={isLoading ? '' : 'Cancel'}
        disabled={!templateTaskName || (this.isTebra
          ? AddTaskTemplate.isProviderBlank(this.state.assignTo) : (!task || !taskDetails || !priority))
          || (isAdmin && (!audienceKey || !!this.getTriggerConflict()))}
        form="addTemplate"
        modalTitle={this.getModalTitle()}
      >
        <div onKeyPress={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
        >
          {this.renderModal()}
        </div>
      </ModalWrapper>
    );
  }
}

AddTaskTemplate.defaultProps = {
  taskTemplateCategoryUsage: [],
  allKareoTaskProviders: [],
};

AddTaskTemplate.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  getTaskTemplates: PropTypes.func.isRequired,
  updateTaskTemplate: PropTypes.func.isRequired,
  createNewTaskTemplate: PropTypes.func.isRequired,
  getEmaTaskProviders: PropTypes.func.isRequired,
  getKareoTaskProviders: PropTypes.func.isRequired,
  allKareoTaskProviders: PropTypes.array,
  allEmaTaskProviders: PropTypes.array.isRequired,
  allDocumentCategories: PropTypes.object.isRequired,
  taskTemplateCategoryUsage: PropTypes.array,
  taskAutoUploadEnabled: PropTypes.bool.isRequired,
  getDocumentCategories: PropTypes.func.isRequired,
  getTaskTemplateCategoryUsage: PropTypes.func.isRequired,
};

export default connect((state) => ({
  allEmaTaskProviders: state.userProfile.allEmaTaskProviders,
  allKareoTaskProviders: state.userProfile.allKareoTaskProviders,
  allDocumentCategories: state.userProfile.allDocumentCategories,
  taskTemplateCategoryUsage: state.userProfile.taskTemplateCategoryUsage,
  isLoading: state.userProfile.isLoading,
}))(AddTaskTemplate);
